import * as React from 'react';
import { apiError } from '@/lib/api-error';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  BadgeCheck,
  CalendarClock,
  Check,
  ChevronDown,
  ChevronRight,
  Clock,
  Loader2,
  Lock,
  PlayCircle,
} from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  bookCertificationCall,
  fetchMyCertification,
  markModuleWatched,
  submitModuleAnswers,
  type CertificationModule,
} from '@/data/advisor-certification';


function ModuleCard({
  module,
  index,
  open,
  onToggle,
  locked,
}: {
  module: CertificationModule;
  index: number;
  open: boolean;
  onToggle: () => void;
  locked: boolean;
}) {
  const queryClient = useQueryClient();
  const [answers, setAnswers] = React.useState<Record<number, number>>({});
  const [result, setResult] = React.useState<{ score: number; passed: boolean } | null>(null);

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ['advisor-certification'] });

  const watched = useMutation({
    mutationFn: () => markModuleWatched(module.slug),
    onSuccess: refresh,
  });

  const submit = useMutation({
    mutationFn: () =>
      submitModuleAnswers(
        module.slug,
        module.questions.map((_, i) => answers[i] ?? -1),
      ),
    onSuccess: (data) => {
      setResult({ score: data.score, passed: data.passed });
      refresh();
      if (data.passed) toast.success(`Passed — ${data.score}%`);
    },
    onError: (error) => toast.error(apiError(error, 'Could not mark that.')),
  });

  const allAnswered = module.questions.every((_, i) => answers[i] !== undefined);

  return (
    <Card
      className={module.passed ? 'border-emerald-200' : ''}
      data-testid={`certification-module-${module.slug}`}
    >
      <CardHeader className="pb-3">
        <button
          type="button"
          onClick={onToggle}
          className="flex w-full items-start gap-3 text-left"
          data-testid={`module-toggle-${module.slug}`}
        >
          <span
            className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
              module.passed
                ? 'bg-emerald-500 text-white'
                : 'border border-muted-foreground/30 text-muted-foreground'
            }`}
          >
            {module.passed ? <Check className="h-4 w-4" /> : index + 1}
          </span>
          <div className="min-w-0 flex-1">
            <CardTitle className="flex flex-wrap items-center gap-2 text-base">
              {module.title}
              {module.passed ? (
                <Badge
                  variant="secondary"
                  className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100"
                  data-testid="module-passed"
                >
                  Passed {module.best_score}%
                </Badge>
              ) : module.attempts > 0 ? (
                <Badge variant="outline" data-testid="module-attempted">
                  {module.last_score}% — {module.pass_mark}% to pass
                </Badge>
              ) : null}
              {module.duration_minutes > 0 && (
                <span className="inline-flex items-center gap-1 text-xs font-normal text-muted-foreground">
                  <Clock className="h-3 w-3" />
                  {module.duration_minutes} min
                </span>
              )}
            </CardTitle>
            <CardDescription>{module.summary}</CardDescription>
          </div>
          {open ? (
            <ChevronDown className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" />
          ) : (
            <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" />
          )}
        </button>
      </CardHeader>

      {open && (
        <CardContent className="space-y-5">
          {module.video_url && (
            <div className="flex flex-wrap items-center gap-3 rounded-md border bg-muted/30 px-3 py-2">
              <PlayCircle className="h-5 w-5 text-amber-600" />
              <a
                href={module.video_url}
                target="_blank"
                rel="noreferrer"
                className="text-sm underline"
                onClick={() => !locked && !module.watched && watched.mutate()}
                data-testid={`module-video-${module.slug}`}
              >
                Watch the video
              </a>
              {module.watched && (
                <Badge variant="outline" className="text-[10px]">
                  Watched
                </Badge>
              )}
            </div>
          )}

          {module.body && (
            <p className="whitespace-pre-wrap text-sm text-muted-foreground">
              {module.body}
            </p>
          )}

          {module.questions.length > 0 && (
            <div className="space-y-4">
              <p className="text-sm font-medium">
                {module.passed ? 'You have passed this one. Take it again if you like.' : 'Questions'}
              </p>
              {module.questions.map((question, questionIndex) => (
                <fieldset key={question.question} className="space-y-1.5">
                  <legend className="text-sm">
                    {questionIndex + 1}. {question.question}
                  </legend>
                  {question.options.map((option, optionIndex) => (
                    <label
                      key={option}
                      className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1 text-sm hover:bg-muted"
                    >
                      <input
                        type="radio"
                        name={`${module.slug}-${questionIndex}`}
                        checked={answers[questionIndex] === optionIndex}
                        onChange={() =>
                          setAnswers({ ...answers, [questionIndex]: optionIndex })
                        }
                        data-testid={`answer-${module.slug}-${questionIndex}-${optionIndex}`}
                      />
                      {option}
                    </label>
                  ))}
                </fieldset>
              ))}

              {result && (
                <p
                  className={`rounded-md px-3 py-2 text-sm ${
                    result.passed
                      ? 'bg-emerald-50 text-emerald-800'
                      : 'bg-amber-50 text-amber-800'
                  }`}
                  data-testid={`module-result-${module.slug}`}
                >
                  {result.passed
                    ? `${result.score}% — passed.`
                    : `${result.score}%. You need ${module.pass_mark}% to pass; try again.`}
                </p>
              )}

              <Button
                size="sm"
                disabled={locked || !allAnswered || submit.isPending}
                onClick={() => submit.mutate()}
                data-testid={`module-submit-${module.slug}`}
              >
                {submit.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {module.passed ? 'Answer again' : 'Submit answers'}
              </Button>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}

export default function CertificationPage() {
  const queryClient = useQueryClient();
  const [openModule, setOpenModule] = React.useState<string | null>(null);
  const [times, setTimes] = React.useState('');
  const [notes, setNotes] = React.useState('');

  const state = useQuery({
    queryKey: ['advisor-certification'],
    queryFn: fetchMyCertification,
    retry: false,
  });

  const book = useMutation({
    mutationFn: () =>
      bookCertificationCall({ preferred_times: times.trim(), notes: notes.trim() }),
    onSuccess: () => {
      setTimes('');
      setNotes('');
      toast.success('Asked BitSwan for a call.');
      queryClient.invalidateQueries({ queryKey: ['advisor-certification'] });
    },
    onError: (error) => toast.error(apiError(error, 'Could not book your call.')),
  });

  if (state.isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const data = state.data;

  if (!data) return null;
  const hasProfile = Boolean(data.has_profile);

  const modules = data.modules ?? [];
  const call = data.call;

  return (
    <div className="space-y-6" data-testid="certification-page">
      <div>
        <h2 className="text-2xl font-bold tracking-tight">Certification</h2>
        <p className="text-muted-foreground">
          Work through the training in your own time. When you have finished
          it, book a call with someone at BitSwan, and then your profile can
          go in the public directory.
        </p>
      </div>

      {!hasProfile && (
        <Card className="border-amber-200 bg-amber-50/40" data-testid="certification-needs-profile">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Step 1: set up your public profile</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap items-center justify-between gap-3 text-sm text-muted-foreground">
            <span>
              The certification call is booked against your public profile. Write it first, then come back for the training and the call.
            </span>
            <Button asChild size="sm" data-testid="certification-go-to-profile">
              <Link to="/advisor/profile">Write my profile</Link>
            </Button>
          </CardContent>
        </Card>
      )}

      {data.is_certified ? (
        <Card className="border-emerald-200 bg-emerald-50/40" data-testid="certification-status">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <BadgeCheck className="h-5 w-5 text-emerald-600" />
              You are a certified advisor
            </CardTitle>
            <CardDescription>
              {data.is_listed
                ? 'Your profile is in the public directory and customers signing up can choose you.'
                : 'Publish your profile and you will appear in the public directory.'}
            </CardDescription>
          </CardHeader>
        </Card>
      ) : (
        <Card className="border-amber-200 bg-amber-50/40" data-testid="certification-status">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">
              Your profile is not in the directory yet
            </CardTitle>
            <CardDescription>
              You can already add clients, map how they work, write specifications, and publish your profile at its own link. Certification adds one thing: strangers can find you. Until then you are not in the directory and customers signing up cannot choose you.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3">
              <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full rounded-full bg-amber-500 transition-all"
                  style={{
                    width: `${data.required_total ? (data.required_passed / data.required_total) * 100 : 0}%`,
                  }}
                />
              </div>
              <span className="text-sm text-muted-foreground" data-testid="training-progress">
                {data.required_passed} of {data.required_total} modules passed
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="space-y-3">
        {modules.map((module, index) => (
          <ModuleCard
            key={module.slug}
            module={module}
            index={index}
            open={openModule === module.slug}
            onToggle={() =>
              setOpenModule(openModule === module.slug ? null : module.slug)
            }
            locked={!hasProfile}
          />
        ))}
      </div>

      {!data.is_certified && (
        <Card data-testid="certification-call-section">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              {data.training_complete ? (
                <CalendarClock className="h-5 w-5 text-amber-600" />
              ) : (
                <Lock className="h-5 w-5 text-muted-foreground" />
              )}
              Your certification call
            </CardTitle>
            <CardDescription>
              {call
                ? 'Booked.'
                : data.training_complete
                  ? 'A half-hour call with someone at BitSwan about the training you have completed.'
                  : 'Available once you have passed every module.'}
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-3">
            {call ? (
              <div className="rounded-md border bg-muted/30 px-3 py-2" data-testid="certification-call">
                <p className="text-sm font-medium">
                  {call.status === 'scheduled' && call.scheduled_at
                    ? `Your call is ${new Date(call.scheduled_at).toLocaleString()}`
                    : 'We have your request and will come back with a time.'}
                </p>
                {call.meeting_url && (
                  <a
                    href={call.meeting_url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs underline"
                    data-testid="certification-meeting-url"
                  >
                    Join the call
                  </a>
                )}
                <p className="mt-1 text-[11px] text-muted-foreground">
                  You told us: {call.preferred_times}
                </p>
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  <Label htmlFor="cert-times">When are you free?</Label>
                  <Textarea
                    id="cert-times"
                    rows={2}
                    value={times}
                    onChange={(e) => setTimes(e.target.value)}
                    disabled={!data.training_complete}
                    placeholder="Weekday mornings CET, or any time Thursday."
                    data-testid="certification-times"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cert-notes">Anything we should know first?</Label>
                  <Textarea
                    id="cert-notes"
                    rows={2}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    disabled={!data.training_complete}
                    placeholder="Optional."
                    data-testid="certification-notes"
                  />
                </div>
                <Button
                  disabled={
                    !data.can_book_call || book.isPending || times.trim().length < 5
                  }
                  onClick={() => book.mutate()}
                  data-testid="certification-book"
                >
                  {book.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Book my certification call
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
